/**
 * pipeline-strip.tsx — Horizontal pipeline step counter.
 *
 * Renders 6 segments (one per OnboardingPackageLifecycle value) each showing
 * the count of packages currently in that state. Segments are ordered to
 * reflect the canonical state machine flow:
 *
 *   draft → collecting → reviewing → approved → released → (blocked is orthogonal)
 *
 * Blocked is shown last as an out-of-band bucket — any package in the
 * 'blocked' lifecycle appears there regardless of prior state.
 *
 * Props are pure — no data fetching. HomeDashboard owns the query.
 */

import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'
import type { OnboardingPackage, OnboardingPackageLifecycle } from '@agentwiki/shared'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PipelineStripProps {
  packages: OnboardingPackage[]
}

// ── Constants ─────────────────────────────────────────────────────────────────

/** Ordered lifecycle steps matching the i18n key map in dashboard.json. */
const PIPELINE_STEPS: OnboardingPackageLifecycle[] = [
  'draft',
  'collecting',
  'reviewing',
  'approved',
  'released',
  'blocked',
]

/** i18n key suffix → pipeline step label (resolved by PipelineStrip via t()). */
const STEP_I18N_KEY: Record<OnboardingPackageLifecycle, string> = {
  draft: 'pipeline.steps.draft',
  collecting: 'pipeline.steps.collecting',
  reviewing: 'pipeline.steps.reviewing',
  approved: 'pipeline.steps.approved',
  released: 'pipeline.steps.released',
  blocked: 'pipeline.steps.blocked',
}

// ── Sub-component: single step segment ────────────────────────────────────────

interface StepSegmentProps {
  label: string
  count: number
  step: OnboardingPackageLifecycle
  isLast: boolean
  isDark: boolean
}

function StepSegment({ label, count, step, isLast, isDark }: StepSegmentProps) {
  const isBlocked = step === 'blocked'
  const hasItems = count > 0

  return (
    <div
      className={cn(
        'flex flex-1 flex-col items-center gap-1 px-2 py-3',
        !isLast && (isDark ? 'border-r border-white/[0.06]' : 'border-r border-neutral-200'),
      )}
    >
      {/* Count badge */}
      <span
        aria-label={`${label}: ${count} ${count === 1 ? 'package' : 'packages'}`}
        className={cn(
          'min-w-[1.5rem] rounded-full px-1.5 py-0.5 text-center text-sm font-semibold tabular-nums',
          isBlocked && hasItems
            ? isDark
              ? 'bg-red-500/20 text-red-300'
              : 'bg-red-100 text-red-700'
            : hasItems
              ? isDark
                ? 'bg-brand-500/20 text-brand-300'
                : 'bg-brand-100 text-brand-700'
              : isDark
                ? 'text-neutral-600'
                : 'text-neutral-400',
        )}
      >
        {count}
      </span>

      {/* Step label */}
      <span
        className={cn(
          'text-center text-[10px] font-medium uppercase tracking-wide',
          isDark ? 'text-neutral-500' : 'text-neutral-500',
        )}
      >
        {label}
      </span>
    </div>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PipelineStrip({ packages }: PipelineStripProps) {
  const { t } = useTranslation('dashboard')
  const { theme } = useAppStore()
  const isDark = theme === 'dark'

  // Bucket packages by lifecycle in one pass — O(n)
  const counts = packages.reduce<Record<OnboardingPackageLifecycle, number>>(
    (acc, pkg) => {
      acc[pkg.lifecycle] = (acc[pkg.lifecycle] ?? 0) + 1
      return acc
    },
    {
      draft: 0,
      collecting: 0,
      reviewing: 0,
      approved: 0,
      released: 0,
      blocked: 0,
    },
  )

  return (
    <section aria-label={t('pipeline.title')}>
      <h2
        className={cn(
          'mb-2 text-xs font-semibold uppercase tracking-wide',
          isDark ? 'text-neutral-500' : 'text-neutral-400',
        )}
      >
        {t('pipeline.title')}
      </h2>

      <div
        className={cn(
          'flex overflow-hidden rounded-xl border',
          isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white',
        )}
      >
        {PIPELINE_STEPS.map((step, idx) => (
          <StepSegment
            key={step}
            label={t(STEP_I18N_KEY[step])}
            count={counts[step]}
            step={step}
            isLast={idx === PIPELINE_STEPS.length - 1}
            isDark={isDark}
          />
        ))}
      </div>
    </section>
  )
}
